import { useState, useEffect } from "react";
import axios from "axios";
import { Users, CheckCircle, XCircle, Eye } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import useAutoRefresh from "../../hooks/useAutoRefresh";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminAgents = () => {
  const { token } = useAuth();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchAgents();
  }, [statusFilter]);

  // Auto-refresh (~1s) — new agent registrations surface instantly
  useAutoRefresh(() => fetchAgents());

  const fetchAgents = async () => {
    try {
      const url = statusFilter === "all" 
        ? `${API}/admin/agents` 
        : `${API}/admin/agents?status=${statusFilter}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgents(response.data);
    } catch (error) {
      console.error("Error fetching agents:", error);
    } finally {
      setLoading(false);
    }
  };

  const approveAgent = async (id) => {
    try {
      await axios.put(`${API}/admin/agents/${id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Agent approved!");
      fetchAgents();
      setSelectedAgent(null);
    } catch (error) {
      console.error("Error approving agent:", error);
      toast.error("Failed to approve agent");
    }
  };

  const blockAgent = async (id) => {
    try {
      await axios.put(`${API}/admin/agents/${id}/block`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Agent blocked!");
      fetchAgents();
      setSelectedAgent(null);
    } catch (error) {
      console.error("Error blocking agent:", error);
      toast.error("Failed to block agent");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending": return "bg-amber-100 text-amber-700";
      case "approved": return "bg-emerald-100 text-emerald-700";
      case "blocked": return "bg-red-100 text-red-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <DashboardLayout type="admin">
      <div className="space-y-6" data-testid="admin-agents">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-2xl font-bold text-gray-900">Manage Agents</h1>
            </div>
            <p className="text-gray-500">Approve and manage agent accounts</p>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 bg-gray-50 border-gray-200 text-gray-800">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="blocked">Blocked</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700" />
              </div>
            ) : agents.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-gray-500">No agents found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50/50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Agent</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Company</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Contact</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Location</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Status</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {agents.map((agent) => (
                      <tr key={agent.id} className="hover:bg-gray-50/30">
                        <td className="px-6 py-4">
                          <p className="text-gray-800 font-medium">{agent.name}</p>
                          <p className="text-gray-400 text-sm">{agent.email}</p>
                        </td>
                        <td className="px-6 py-4 text-gray-600">
                          {agent.company_name || "-"}
                        </td>
                        <td className="px-6 py-4 text-gray-600">
                          {agent.phone || "-"}
                        </td>
                        <td className="px-6 py-4 text-gray-500 text-sm">
                          {agent.city && agent.state ? `${agent.city}, ${agent.state}` : "-"}
                        </td>
                        <td className="px-6 py-4">
                          <Badge className={`capitalize ${getStatusColor(agent.status)}`}>
                            {agent.status}
                          </Badge>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => setSelectedAgent(agent)}
                              className="text-gray-500 hover:text-gray-900"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {agent.status === "pending" && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => approveAgent(agent.id)}
                                className="text-green-400 hover:text-green-300"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            )}
                            {agent.status !== "blocked" && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => blockAgent(agent.id)}
                                className="text-red-400 hover:text-red-300"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Agent Detail Dialog */}
      <Dialog open={!!selectedAgent} onOpenChange={() => setSelectedAgent(null)}>
        <DialogContent className="max-w-lg bg-white border-gray-200 text-gray-800">
          {selectedAgent && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">Agent Details</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-gray-500 text-sm">Name</p>
                    <p className="font-medium">{selectedAgent.name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Status</p>
                    <Badge className={getStatusColor(selectedAgent.status)}>
                      {selectedAgent.status}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Email</p>
                    <p>{selectedAgent.email}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Phone</p>
                    <p>{selectedAgent.phone}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-gray-500 text-sm">Company</p>
                    <p>{selectedAgent.company_name}</p>
                  </div>
                  {selectedAgent.gst_number && (
                    <div>
                      <p className="text-gray-500 text-sm">GST Number</p>
                      <p>{selectedAgent.gst_number}</p>
                    </div>
                  )}
                  {selectedAgent.pan_number && (
                    <div>
                      <p className="text-gray-500 text-sm">PAN Number</p>
                      <p>{selectedAgent.pan_number}</p>
                    </div>
                  )}
                  <div className="col-span-2">
                    <p className="text-gray-500 text-sm">Address</p>
                    <p>{selectedAgent.address}, {selectedAgent.city}, {selectedAgent.state}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Registered On</p>
                    <p>{(() => { const d = new Date(selectedAgent.created_at); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; })()}</p>
                  </div>
                </div>
                <div className="flex gap-2 pt-4">
                  {selectedAgent.status === "pending" && (
                    <Button 
                      onClick={() => approveAgent(selectedAgent.id)}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" /> Approve
                    </Button>
                  )}
                  {selectedAgent.status !== "blocked" && (
                    <Button 
                      onClick={() => blockAgent(selectedAgent.id)}
                      className="flex-1 bg-red-600 hover:bg-red-700"
                    >
                      <XCircle className="w-4 h-4 mr-2" /> Block
                    </Button>
                  )}
                  {selectedAgent.status === "blocked" && (
                    <Button 
                      onClick={() => approveAgent(selectedAgent.id)}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" /> Unblock
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default AdminAgents;
